import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface DateTimePickerFieldProps {
  id?: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  min?: string;
  hint?: string;
  formatLabel?: string;
  optionalLabel?: string;
  clearButtonLabel?: string;
  invalid?: boolean;
  errorMessage?: string | null;
}

const MINUTE_STEP = 5;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

function parseDateTime(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(" ", "T");
  const withTime = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (withTime) {
    const [, y, m, d, hh, mm] = withTime;
    return new Date(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), 0, 0);
  }

  const onlyDate = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (onlyDate) {
    const [, y, m, d] = onlyDate;
    return new Date(Number(y), Number(m) - 1, Number(d), 0, 0, 0, 0);
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatForService(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toMondayIndex(day: number): number {
  return day === 0 ? 6 : day - 1;
}

export function DateTimePickerField({
  id,
  label,
  value,
  onChange,
  min,
  hint,
  formatLabel,
  optionalLabel,
  clearButtonLabel,
  invalid,
  errorMessage,
}: DateTimePickerFieldProps) {
  const generatedId = useId();
  const inputId = id || `memeet-datetime-${generatedId}`;
  const triggerId = `${inputId}-trigger`;
  const panelId = `${inputId}-panel`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = [hint ? hintId : "", errorMessage ? errorId : ""]
    .filter(Boolean)
    .join(" ");
  const containerRef = useRef<HTMLDivElement>(null);

  const parsedValue = useMemo(() => parseDateTime(value), [value]);
  const parsedMin = useMemo(() => parseDateTime(min || ""), [min]);
  const minDay = useMemo(() => (parsedMin ? startOfDay(parsedMin) : null), [parsedMin]);

  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(
    parsedValue ? startOfMonth(parsedValue) : startOfMonth(new Date())
  );
  const [draftDate, setDraftDate] = useState<Date | null>(parsedValue ? startOfDay(parsedValue) : null);
  const [draftHour, setDraftHour] = useState<number>(parsedValue ? parsedValue.getHours() : 9);
  const [draftMinute, setDraftMinute] = useState<number>(parsedValue ? parsedValue.getMinutes() : 0);

  const syncDraftFromCurrentValue = useCallback(() => {
    const current = parseDateTime(value);
    const nextMonth = current ? startOfMonth(current) : startOfMonth(new Date());
    setViewMonth(nextMonth);
    setDraftDate(current ? startOfDay(current) : null);
    setDraftHour(current ? current.getHours() : 9);
    setDraftMinute(current ? current.getMinutes() : 0);
  }, [value]);

  useEffect(() => {
    if (!isOpen) {
      syncDraftFromCurrentValue();
    }
  }, [isOpen, syncDraftFromCurrentValue]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const weekDayLabels = useMemo(() => {
    const monday = new Date(2024, 0, 1); // Monday
    return Array.from({ length: 7 }, (_, index) =>
      new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(
        new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + index)
      )
    );
  }, []);

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(viewMonth),
    [viewMonth]
  );

  const calendarCells = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const leading = toMondayIndex(new Date(year, month, 1).getDay());
    const total = Math.ceil((leading + daysInMonth) / 7) * 7;

    return Array.from({ length: total }, (_, index) => {
      const day = index - leading + 1;
      if (day < 1 || day > daysInMonth) {
        return null;
      }
      return new Date(year, month, day, 0, 0, 0, 0);
    });
  }, [viewMonth]);

  const isDayDisabled = useCallback(
    (date: Date) => {
      if (!minDay) return false;
      return date.getTime() < minDay.getTime();
    },
    [minDay]
  );

  const minAllowedMinuteOfDay = useMemo(() => {
    if (!parsedMin || !draftDate) return null;
    if (!isSameDay(parsedMin, draftDate)) return null;
    return parsedMin.getHours() * 60 + parsedMin.getMinutes();
  }, [parsedMin, draftDate]);

  const isTimeDisabled = useCallback(
    (hour: number, minute: number) => {
      if (!draftDate) return true;
      if (minAllowedMinuteOfDay == null) return false;
      return hour * 60 + minute < minAllowedMinuteOfDay;
    },
    [draftDate, minAllowedMinuteOfDay]
  );

  const handleApply = useCallback(() => {
    if (!draftDate) return;
    const next = new Date(draftDate);
    next.setHours(draftHour, draftMinute, 0, 0);

    if (parsedMin && next.getTime() < parsedMin.getTime()) {
      return;
    }

    onChange(formatForService(next));
    setIsOpen(false);
  }, [draftDate, draftHour, draftMinute, parsedMin, onChange]);

  const handleClear = useCallback(() => {
    onChange("");
    syncDraftFromCurrentValue();
    setIsOpen(false);
  }, [onChange, syncDraftFromCurrentValue]);

  const handleUseNow = useCallback(() => {
    const now = new Date();
    const roundedMinute = Math.floor(now.getMinutes() / MINUTE_STEP) * MINUTE_STEP;
    setDraftDate(startOfDay(now));
    setDraftHour(now.getHours());
    setDraftMinute(roundedMinute);
    setViewMonth(startOfMonth(now));
  }, []);

  const displayValue = useMemo(() => {
    if (!parsedValue) return "";
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(parsedValue);
  }, [parsedValue]);

  const minuteOptions = useMemo(
    () => Array.from({ length: 60 / MINUTE_STEP }, (_, index) => index * MINUTE_STEP),
    []
  );

  const isApplyDisabled = !draftDate || isTimeDisabled(draftHour, draftMinute);
  const portalTarget = useMemo(() => {
    if (typeof document === "undefined") return null;
    return (
      containerRef.current?.closest(".memeet-root") ||
      containerRef.current ||
      document.body
    );
  }, [isOpen]);

  return (
    <div className="create-room-field date-time-field" ref={containerRef}>
      <div className="field-label-row">
        <label htmlFor={triggerId}>{label}</label>
        <div className="field-meta-badges">
          {formatLabel ? <span className="field-meta-chip">{formatLabel}</span> : null}
          {optionalLabel ? <span className="field-optional-chip">{optionalLabel}</span> : null}
        </div>
      </div>

      <button
        type="button"
        id={triggerId}
        className={`datetime-trigger${invalid ? " invalid" : ""}`}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy || undefined}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => {
          if (!isOpen) {
            syncDraftFromCurrentValue();
          }
          setIsOpen((prev) => !prev);
        }}
      >
        <span className="datetime-trigger-left">
          <span className="datetime-trigger-icon" aria-hidden="true">
            <CalendarIcon />
          </span>
          <span className={`datetime-trigger-value${displayValue ? "" : " placeholder"}`}>
            {displayValue || formatLabel || "YYYY-MM-DD HH:mm"}
          </span>
        </span>
        <span className={`datetime-trigger-arrow${isOpen ? " open" : ""}`} aria-hidden="true">
          <ChevronIcon />
        </span>
      </button>

      {isOpen && portalTarget
        ? createPortal(
            <div
              className="datetime-modal-overlay"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setIsOpen(false);
                }
              }}
            >
              <div
                className={`datetime-popover${invalid ? " invalid" : ""}`}
                id={panelId}
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${panelId}-title`}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="datetime-modal-header">
                  <span className="datetime-modal-title" id={`${panelId}-title`}>
                    {label}
                  </span>
                  <button
                    type="button"
                    className="datetime-modal-close"
                    onClick={() => setIsOpen(false)}
                    aria-label="Close"
                  >
                    <CloseIcon />
                  </button>
                </div>

                <div className="datetime-calendar-head">
                  <button
                    type="button"
                    className="datetime-nav-btn"
                    onClick={() =>
                      setViewMonth(
                        (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1, 0, 0, 0, 0)
                      )
                    }
                    aria-label="Previous month"
                  >
                    <ChevronLeftIcon />
                  </button>

                  <div className="datetime-month-label">{monthLabel}</div>

                  <button
                    type="button"
                    className="datetime-nav-btn"
                    onClick={() =>
                      setViewMonth(
                        (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1, 0, 0, 0, 0)
                      )
                    }
                    aria-label="Next month"
                  >
                    <ChevronRightIcon />
                  </button>
                </div>

                <div className="datetime-weekdays">
                  {weekDayLabels.map((weekDay) => (
                    <span key={weekDay}>{weekDay}</span>
                  ))}
                </div>

                <div className="datetime-days-grid">
                  {calendarCells.map((cellDate, index) => {
                    if (!cellDate) {
                      return <span key={`empty-${index}`} className="datetime-day-empty" />;
                    }

                    const disabled = isDayDisabled(cellDate);
                    const selected = draftDate ? isSameDay(draftDate, cellDate) : false;
                    const today = isSameDay(cellDate, new Date());

                    return (
                      <button
                        key={cellDate.toISOString()}
                        type="button"
                        className={`datetime-day-btn${selected ? " selected" : ""}${today ? " today" : ""}`}
                        disabled={disabled}
                        onClick={() => setDraftDate(cellDate)}
                      >
                        {cellDate.getDate()}
                      </button>
                    );
                  })}
                </div>

                <div className="datetime-time-area">
                  <div className="datetime-time-column">
                    <span className="datetime-time-label">Hour</span>
                    <div className="datetime-time-list">
                      {Array.from({ length: 24 }, (_, hour) => (
                        <button
                          key={`hour-${hour}`}
                          type="button"
                          className={`datetime-time-btn${draftHour === hour ? " selected" : ""}`}
                          disabled={isTimeDisabled(hour, draftMinute)}
                          onClick={() => setDraftHour(hour)}
                        >
                          {pad(hour)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="datetime-time-column">
                    <span className="datetime-time-label">Minute</span>
                    <div className="datetime-time-list">
                      {minuteOptions.map((minute) => (
                        <button
                          key={`minute-${minute}`}
                          type="button"
                          className={`datetime-time-btn${draftMinute === minute ? " selected" : ""}`}
                          disabled={isTimeDisabled(draftHour, minute)}
                          onClick={() => setDraftMinute(minute)}
                        >
                          {pad(minute)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="datetime-actions">
                  <button type="button" className="datetime-link-btn" onClick={handleUseNow}>
                    Now
                  </button>
                  <button type="button" className="datetime-link-btn" onClick={handleClear}>
                    {clearButtonLabel || "Clear"}
                  </button>
                  <button
                    type="button"
                    className="datetime-apply-btn"
                    onClick={handleApply}
                    disabled={isApplyDisabled}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>,
            portalTarget
          )
        : null}

      {hint ? (
        <span className="field-hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {errorMessage ? (
        <span className="field-error" id={errorId} role="alert">
          {errorMessage}
        </span>
      ) : null}
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="3" ry="3" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 7l5 6 5-6" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 5l-5 5 5 5" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M8 5l5 5-5 5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}
